import { addCommaToPrice } from '../../../../helpers/numbers'
import { getTranslate } from '../../../../localization'
import {
  BookabilityStatus,
  Occupancy,
  Price
} from '../../../../types/searchAvailability'

/**
 * The "compare-at" price — the number we strike through — per ADR-040 §2.3/§2.5.
 *
 * Returns the amount only when it is STRICTLY ABOVE the price actually shown;
 * otherwise null (render no strikethrough). Two cases collapse into that rule:
 * equal (nothing to advertise) and below (a struck price under the charged price
 * reads as a discount while actually being an increase).
 *
 * `!==` is deliberately NOT used: it passes when the anchor is *lower* than the
 * shown price, which is exactly the defect ADR-040 exists to remove.
 * `beforeDiscount` may be absent once the producer omits it, so presence is
 * checked before comparing.
 *
 * No currency check is needed here, and its absence is deliberate: both figures
 * come from the same `Price` object and therefore share its single `currency`
 * field. ADR-040 §2.5's cross-currency precondition applies to comparing
 * separate `rateBreakouts[]` entries, which each carry their own currency — not
 * to this pair.
 */
export function compareAtTotal(price: Price): number | null {
  const anchor = price?.beforeDiscount?.totalPrice
  const shown = price?.afterDiscount?.totalPrice

  if (
    anchor == null ||
    shown == null ||
    !Number.isFinite(anchor) ||
    !Number.isFinite(shown)
  ) {
    return null
  }

  return anchor > shown ? anchor : null
}

/**
 * Whole-percent discount off the compare-at price, or null when there is no
 * honest discount to state. Returns either null or a finite value in (0, 100].
 *
 * Two independent guards are required, and conflating them is a bug:
 *
 *  1. `compareAtTotal` must be non-null — the badge only makes sense beside a
 *     rendered strikethrough. That gate is `totalPrice`-based.
 *  2. The ROOM component must itself have fallen. `totalPrice` is the sum of
 *     room + children + extra-bed + add-ons, so a genuine total-level discount
 *     does NOT prove `roomPrice` fell too; it can even have risen. Dividing on
 *     `roomPrice` while gating on `totalPrice` would then emit a negative
 *     percentage ("-6% off") beside a correctly-struck price — the same
 *     misleading-discount class of defect ADR-040 exists to remove.
 *
 * The percentage keeps this app's legacy room-price arithmetic; only the guard
 * is tightened.
 */
export function discountPercent(price: Price): number | null {
  if (compareAtTotal(price) == null) {
    return null
  }

  const anchorRoom = price?.beforeDiscount?.roomPrice
  const shownRoom = price?.afterDiscount?.roomPrice

  if (
    anchorRoom == null ||
    shownRoom == null ||
    !Number.isFinite(anchorRoom) ||
    !Number.isFinite(shownRoom)
  ) {
    return null
  }

  // Guard the field this ratio actually divides on — not the one the
  // strikethrough gate used.
  if (anchorRoom <= 0 || shownRoom < 0 || anchorRoom <= shownRoom) {
    return null
  }

  return Math.round((1 - shownRoom / anchorRoom) * 100)
}

export function getBookabilityLabel(
  status: BookabilityStatus,
  occupancy: Occupancy
): string {
  switch (status) {
    case 'BOOKABLE':
      return 'Bookable'
    case 'NO_AVAILIBILITY':
      return 'No Availability'
    case 'RESTRICTIONS':
      return 'Unavailable due to restrictions'
    case 'NO_PRICE':
      return `No available rate for ${
        occupancy.adults + occupancy.extraBeds
      } adults`
    case 'NO_PAYMENTTERM':
      return 'No Payment Term'
    case 'NOT_INCLUDED_IN_CREDITORLIST':
      return 'Not Included In Creditor List'
    case 'WALLET_TYPE_NOT_SUPPORTED':
      return 'Wallet type not accepted for this rate'
    default:
      return status // fallback, but ideally never hit
  }
}

/**
 * What to tell the user when the room counter cannot be incremented.
 *
 * The counter's `max` is the *effective* availability — allocated or pooled,
 * depending on the "Use of Allocated" checkbox — and CustomCounter silently
 * refuses to go past it. At max = 0 that renders as a `+` that does nothing and
 * explains nothing, which is indistinguishable from a broken button. Observed on
 * staging: a rate plan showing a price and a bookable status, with every room
 * allocated to a channel and none left in the pool, so `+` was inert with no
 * indication why.
 *
 * The distinction that matters is between "you cannot book this" and "you cannot
 * book this THE WAY YOU CURRENTLY HAVE IT SET" — the second is recoverable by
 * ticking one checkbox, and is the case worth naming explicitly.
 */
export type AvailabilityNotice =
  | { kind: 'none' }
  | { kind: 'use-allocated'; allocated: number }
  | { kind: 'use-pool'; pooled: number }
  | { kind: 'sold-out' }

export function availabilityNotice(input: {
  bookabilityStatus: BookabilityStatus
  useAllocated: boolean
  pooledAvailability: number
  allocatedAvailability: number
}): AvailabilityNotice {
  // A non-bookable rate plan already renders its own reason in place of the
  // counter, so a second message here would contradict or duplicate it.
  if (input.bookabilityStatus !== 'BOOKABLE') return { kind: 'none' }

  const effective = input.useAllocated
    ? input.allocatedAvailability
    : input.pooledAvailability

  // Room to book: the counter works, say nothing.
  if (effective > 0) return { kind: 'none' }

  // Nothing in the bucket being counted, but rooms exist in the OTHER one. One
  // checkbox away from bookable — name the remedy rather than the symptom.
  //
  // Both directions are handled, and the second is not hypothetical: the
  // checkbox is disabled once allocatedAvailability hits 0, but nothing unticks
  // it, so a refetch that drains the allocated bucket leaves `useAllocated`
  // stranded at true. Reporting "sold out" there would be false whenever the
  // pool still has rooms — the same blindness this function exists to remove,
  // pointing the other way.
  if (!input.useAllocated && input.allocatedAvailability > 0) {
    return { kind: 'use-allocated', allocated: input.allocatedAvailability }
  }
  if (input.useAllocated && input.pooledAvailability > 0) {
    return { kind: 'use-pool', pooled: input.pooledAvailability }
  }

  return { kind: 'sold-out' }
}

/**
 * Render a balance figure, or say it is unknown.
 *
 * Deliberately NOT defaulted to zero. This figure tells an operator whether a booking can
 * be funded; a confident "0" for a wallet that holds money is a worse answer than an
 * explicit unknown, and that fabricated zero is exactly what the tooltip showed for every
 * rate plan on a channel that hides provider names.
 *
 * A balance is unknown when the wallet is known to fund the rate plan but WHICH of its
 * creditors does so is not knowable client-side -- see balancesForRatePlan.
 */
export function formatBalance(
  value: number | undefined,
  currency: string | undefined
): string {
  if (value === undefined) {
    return getTranslate('Unknown')
  }
  return `${addCommaToPrice(value)} ${getTranslate(currency || '')}`
}
